import { Box, Drawer, Text } from '@mantine/core'
import { CodeHighlight } from '@mantine/code-highlight'
import { TRawPacket } from 'api/packets/getRawPacketList'
import Datatable, { TDatatable } from 'components/datatable'
import { DatatableFilter } from 'components/datatable/DatatableFilter'
import {
  RefetchButton,
  RefetchButtonProps,
} from 'components/common/RefetchButton'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { drawerStyles } from 'utils/drawerStyles'

const getPayload = (packet: TRawPacket) =>
  typeof packet.dataAsciiParsed === 'string'
    ? packet.data
    : packet.dataAsciiParsed

const formatPayload = (packet: TRawPacket, spacing?: number) => {
  const payload = getPayload(packet)
  return typeof payload === 'string'
    ? payload
    : JSON.stringify(payload, null, spacing)
}

const formatPreview = (packet: TRawPacket) => {
  const payload = formatPayload(packet)
  const maximumLength = 240

  return payload.length > maximumLength
    ? `${payload.slice(0, maximumLength)}…`
    : payload
}

const formatPacketDetails = (packet: TRawPacket) =>
  JSON.stringify(
    {
      context: {
        packetId: packet.id,
        deviceId: packet.deviceId,
        receivedAt: new Date(packet.tmst).toISOString(),
      },
      payload: getPayload(packet),
    },
    null,
    2,
  )

export const MqttPacketTable = ({
  data,
  dataUpdatedAt,
  refetch,
  isFetching,
}: {
  data: TRawPacket[]
} & RefetchButtonProps) => {
  const { t } = useTranslation()
  const [selectedPacket, setSelectedPacket] = useState<TRawPacket | null>(null)

  const headers: TDatatable<TRawPacket>[] = [
    {
      id: 'time',
      label: t('generic.time'),
      colSize: 1.5,
      selector: (item, { dayjs }) => (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {dayjs(item.tmst).format('DD.MM, HH:mm:ss')}
        </Text>
      ),
    },
    {
      id: 'payload',
      label: t('generic.data'),
      colSize: 10,
      selector: item => (
        <Box
          style={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <CodeHighlight language="json" code={formatPreview(item)} />
        </Box>
      ),
    },
  ]

  return (
    <>
      <DatatableFilter
        datatableId="mqttPacketTable"
        headers={headers}
        initialPreset={['time', 'payload']}
        rightSection={
          <RefetchButton
            refetch={refetch}
            dataUpdatedAt={dataUpdatedAt}
            isFetching={isFetching}
          />
        }
      >
        {filteredHeaders => (
          <Datatable
            data={data}
            headers={filteredHeaders}
            fixedLayout
            onClick={packet => setSelectedPacket(packet)}
          />
        )}
      </DatatableFilter>
      <Drawer
        styles={drawerStyles}
        padding="lg"
        position="right"
        opened={selectedPacket !== null}
        onClose={() => setSelectedPacket(null)}
        title={t('packet.eventDetails')}
      >
        {selectedPacket && (
          <CodeHighlight
            withLineNumbers
            language="json"
            copyLabel={t('generic.copy')!}
            copiedLabel={t('generic.copied')!}
            code={formatPacketDetails(selectedPacket)}
          />
        )}
      </Drawer>
    </>
  )
}
